import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

interface TenantDomain {
  id: string;
  host: string;
  status: 'pending' | 'approved' | 'rejected';
  isPrimary: boolean;
  createdAt: string;
  reviewedAt: string | null;
  tenantId: string;
}

export default function ClientDomainsTab({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const { t } = useTranslation();
  const [domains, setDomains]   = useState<TenantDomain[]>([]);
  const [loading, setLoading]   = useState(true);
  const [newHost, setNewHost]   = useState('');
  const [adding, setAdding]     = useState(false);
  const [error, setError]       = useState('');

  const load = async () => {
    try {
      setLoading(true);
      // /admin/domains no filtra por tenant server-side (solo ?status=) —
      // filtramos client-side; el volumen de dominios totales es bajo.
      const { data } = await api.get('/admin/domains');
      setDomains((data as TenantDomain[]).filter(d => d.tenantId === tenantId));
    } catch {
      setError(t('domains.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tenantId]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdding(true);
    setError('');
    try {
      await api.post(`/admin/tenants/${tenantId}/domains`, { host: newHost.trim().toLowerCase() });
      setNewHost('');
      load();
    } catch (err: any) {
      setError(err.response?.status === 409 ? t('domains.form.duplicateError') : t('domains.form.genericError'));
    } finally {
      setAdding(false);
    }
  };

  const review = async (id: string, status: 'approved' | 'rejected') => {
    if (status === 'rejected' && !window.confirm(t('domains.admin.confirmReject'))) return;
    await api.patch(`/admin/domains/${id}`, { status });
    load();
  };

  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>{t('domains.admin.title', { tenant: tenantName })}</h2>

      {error && <div className="error-message" style={{ marginBottom: 14 }}>{error}</div>}

      {loading ? (
        <div style={{ color: 'var(--gray)', fontSize: 13, padding: '12px 0' }}>...</div>
      ) : domains.length === 0 ? (
        <div style={{ color: 'var(--gray)', fontSize: 13, padding: '12px 0' }}>{t('domains.admin.noDomains')}</div>
      ) : (
        <div style={{ marginBottom: 16, background: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
          {domains.map((d, i) => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: i < domains.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.host}</div>
              </div>
              <DomainStatusPill status={d.status} />
              {d.status === 'pending' && (
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={() => review(d.id, 'approved')}
                    style={{ fontSize: 11, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--green)', background: 'var(--green-bg)', color: 'var(--green-dk)', cursor: 'pointer' }}
                  >
                    {t('domains.admin.approve')}
                  </button>
                  <button
                    onClick={() => review(d.id, 'rejected')}
                    style={{ fontSize: 11, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--gray)', cursor: 'pointer' }}
                  >
                    {t('domains.admin.reject')}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, maxWidth: 420 }}>
        <input
          className="settings-input"
          value={newHost}
          onChange={e => setNewHost(e.target.value)}
          placeholder={t('domains.admin.addPlaceholder')}
          required
          style={{ flex: 1 }}
        />
        <button type="submit" disabled={adding} className="primary-button" style={{ padding: '9px 16px', fontSize: 12, flexShrink: 0, whiteSpace: 'nowrap' }}>
          {adding ? t('domains.admin.adding') : t('domains.admin.add')}
        </button>
      </form>
      <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 8 }}>{t('domains.admin.addDirect')}</div>
    </div>
  );
}

function DomainStatusPill({ status }: { status: 'pending' | 'approved' | 'rejected' }) {
  const { t } = useTranslation();
  const colors: Record<string, { bg: string; fg: string }> = {
    pending:  { bg: 'var(--amber-bg)', fg: 'var(--amber)' },
    approved: { bg: 'var(--green-bg)', fg: 'var(--green-dk)' },
    rejected: { bg: 'var(--red-bg)',   fg: 'var(--red)' },
  };
  const c = colors[status];
  return (
    <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 99, background: c.bg, color: c.fg, flexShrink: 0 }}>
      {t(`domains.status.${status}`)}
    </span>
  );
}
